import { GameModel } from "@westpierstudio/luma/game";
import { reset, Resettable } from "@westpierstudio/luma/utils/reset";
import { SpinModeType, SpinSpeedType } from "@westpierstudio/core-module-oryx/components/ui/slot-game-ui/UIStateMachine";
import { OverlaySymbolState, ReelsModel, SpinAction, SpinType } from "components/reels/ReelsModel";
import { WinDisplayConfig, WinlinesModel } from "components/winlines/WinlinesModel";
import { FREE_ROUND, OpenGameData, OpenGameEvent, RTPVariant, SpreadRTPs, VarientName } from "servertypes/gameevents/OpenGame";
import { SpinEvent, SpinGamePlayData } from "servertypes/gameevents/Spin";
import { VSSpinEvent, VSSpinGamePlayData } from "servertypes/gameevents/VSSpin";
import { FreeSpinEvent, FreeSpinGamePlayData } from "servertypes/gameevents/FreeSpin";
import { BaseGameEventData, FinalPersistingState, LastEventData, PayLineWin, VSOption, VSResult, SymbolPos } from "servertypes/SharedTypes";
import { getWinType, isBigWinType } from "@westpierstudio/core-module-oryx/utils/win-countup";
import { cloneDeep } from "lodash";
import { devLog } from "utils/Misc";
import { VSHistory } from "components/vsscreen/VSHistory";
import { SmallSpinEvent } from "servertypes/gameevents/SmallSpin";

export enum GameState {
    INIT = `INIT`,
    IDLE = `IDLE`,
    SPIN = `SPIN`,
    SHOW_WINS = `SHOW_WINS`,
    SHOW_BIG_WIN = `SHOW_BIG_WIN`,
    VS_SPIN = `VS_SPIN`,
    VS_SPIN_SHOW_WINS = `VS_SPIN_SHOW_WINS`,
    VS_SPIN_SHOW_BIG_WIN = `VS_SPIN_SHOW_BIG_WIN`,
    FREE_SPIN = `FREE_SPIN`,
    FREE_SPIN_SHOW_WINS = `FREE_SPIN_SHOW_WINS`,
    FREE_SPIN_SHOW_BIG_WIN = `FREE_SPIN_SHOW_BIG_WIN`
}

export type VSResultData = {
    result: VSResult,
    wins: {dragon: number, panda: number},
    pick: VSOption
}

export type VSHistoryResult = {
    value: number,
    type: "WIN" | "LOSS" | "DRAW"
}

export type VSHistoryData = {
    vsResults: {
        dragons: VSHistoryResult[],
        pandas: VSHistoryResult[]
    }
}

export type SpinCompleteWaiter = {
    waiting: boolean;
    accumulatedMoneyWon: number;
}

export class DragonsVsPandasGameModel extends GameModel {
    private resetList: Resettable[] = [];
    private _openGameEvent?: OpenGameEvent;
    private _state = GameState.INIT;
    private _activeWinDisplayConfig!: WinDisplayConfig;
    private winDisplayConfigs: Map<SpinType, WinDisplayConfig> = new Map();
    reels!: ReelsModel;
    winlines!: {dragons: WinlinesModel, pandas: WinlinesModel};
    spinSpeedType = SpinSpeedType.REGULAR;
    spinModeType = SpinModeType.MANUAL;
    bigWin = false;
    vsPickIndex?: VSOption = undefined;
    vsResultData?: VSResultData = undefined;
    vsHistoryData?: VSHistoryData = undefined;
    haveEnteredFreeSpins: boolean = false;
    enteringVS: boolean = false;
    spinCompleteWaiter: SpinCompleteWaiter = {waiting: false, accumulatedMoneyWon: 0};
    rtpType: RTPVariant = `spread1`;
    rtp?: number;

    get openGameData() {
        return this._openGameEvent?.body.data as OpenGameData;
    }

    get anticipation(): boolean {
        return false;
    }

    get state() {
        return this._state;
    }

    set state(state: GameState) {

        devLog(`New State: ${state}`);
        this._state = state;
    }

    get inFreeSpinBonus(): boolean {
        return (
            this._state === GameState.FREE_SPIN ||
            this._state === GameState.FREE_SPIN_SHOW_WINS ||
            this._state === GameState.FREE_SPIN_SHOW_BIG_WIN
        );
    }

    get activeWinDisplayConfig(): WinDisplayConfig {
        return this._activeWinDisplayConfig;
    }

    get timeBetweenWinsActive(): boolean {

        const totalLineCount = this.winlines.dragons.lineCount + this.winlines.pandas.lineCount;

        return (totalLineCount > 1) || this.state === GameState.IDLE;
    }

    getSpinType(spinAction: SpinAction) {
        return this.reels.getSpinType(spinAction, this.spinSpeedType);
    }

    override setup(): void {
        super.setup();

        this.resetList.push(this.reels = new ReelsModel(this.services));

        this.winlines = {
            dragons: new WinlinesModel(this.services),
            pandas: new WinlinesModel(this.services)
        };

        this.resetList.push(this.winlines.dragons);
        this.resetList.push(this.winlines.pandas);

        this.setupWinDisplayConfigs();
    }

    processOpenGameEvent(event: OpenGameEvent): void {
        this._openGameEvent = event;

        const {lastEvent} = event.body;

        if (lastEvent) {

            this.reels.processLastEventData(lastEvent.data);
            this.vsHistoryData = this.processVSHistory(lastEvent.data.finalPersistingState);
        }

        this.extractRTPVariantData(event);
    }

    extractRTPVariantData(event: OpenGameEvent) {
        const {variantName, rtp} = event.body.data;

        if (variantName === undefined) {
            console.warn(`No variantName data in openGame event - falling back to default rtp type of ${this.rtpType} lines`);
            return;
        }

        if (typeof rtp !== `number`) {
            const isNoSpread = rtp.SMALL_SPIN === undefined;
            this.rtpType = isNoSpread ? `spread0`: `spread1`;
        } else {

            console.warn(`Unrecognised rtp type - falling back to default rtp type of ${this.rtpType} lines`);
        }

        const rtpValue = Number(variantName.replace(`rtp`, ``));
        if (!isNaN(rtpValue)) {
            this.rtp = rtpValue;
            console.log(`RTP Variant: ${rtpValue}`);
        } else {
            console.warn(`Unrecognised rtp`);
        }
    }

    processSpinEvent(event: SpinEvent | SmallSpinEvent): void {
        const {betData, gameplay, totalMoneyWon, nextActions} = event.body.data;
        const {dragonsPaylineWins, pandasPaylineWins, dragonsMultipliedWins, pandasMultipliedWins} = gameplay;

        this.updateEnteringVS(event);
        this.reels.processSpinEvent(event);

        const winType = getWinType(betData.playerBet, totalMoneyWon);
        this.bigWin = isBigWinType(winType);

        this.winlines.dragons.reset();
        this.winlines.pandas.reset();

        if (!nextActions) {
            this.winlines.dragons.storeWins(dragonsPaylineWins, dragonsMultipliedWins);
            this.winlines.pandas.storeWins(pandasPaylineWins, pandasMultipliedWins);
        }
    }

    processVSSpinEvent(event: VSSpinEvent): void {

        const {betData, gameplay, totalMoneyWon} = event.body.data;
        const {dragonsPaylineWins, pandasPaylineWins, jackpotWin} = gameplay;

        this.reels.processVSSpinEvent(event, this.haveEnteredFreeSpins);
        this.vsResultData = this.processVSResults(
            dragonsPaylineWins,
            pandasPaylineWins,
            this.vsPickIndex as VSOption,
            jackpotWin
        );

        this.winlines.dragons.reset();
        this.winlines.pandas.reset();

         //The server includes wins for the non-picked side in the response, but we don't wan't to show those
         if (this.vsPickIndex === VSOption.DRAGON) {

            this.reels.setWinningSymbolState(dragonsPaylineWins, []);

            this.winlines.dragons.storeWins(dragonsPaylineWins);
            this.winlines.pandas.storeWins([]);

        } else if (this.vsPickIndex === VSOption.PANDA) {

            this.reels.setWinningSymbolState([], pandasPaylineWins);

            this.winlines.dragons.storeWins([]);
            this.winlines.pandas.storeWins(pandasPaylineWins);
        }

        this.vsPickIndex = undefined;

        const winType = getWinType(betData.playerBet, totalMoneyWon);
        this.bigWin = isBigWinType(winType);
    }

    processFreeSpinEvent(event: FreeSpinEvent): void {

        const {betData, gameplay, totalMoneyWon, nextActions} = event.body.data;
        const {dragonsPaylineWins, pandasPaylineWins, dragonsMultipliedWins, pandasMultipliedWins} = gameplay;

        this.updateEnteringVS(event);
        this.reels.processFreeSpinEvent(event);

        const winType = getWinType(betData.playerBet, totalMoneyWon);
        this.bigWin = isBigWinType(winType);

        this.winlines.dragons.reset();
        this.winlines.pandas.reset();

        if (!nextActions || nextActions[0] === `FREE_SPIN`) {
            this.winlines.dragons.storeWins(dragonsPaylineWins, dragonsMultipliedWins);
            this.winlines.pandas.storeWins(pandasPaylineWins, pandasMultipliedWins);
        }
    }

    updateEnteringVS(event: SpinEvent | SmallSpinEvent | FreeSpinEvent): void {
        if (event.body.data.nextActions) {
            if (event.body.data.nextActions[0] === `VS_SPIN`) {
                this.enteringVS = true;
                this.vsHistoryData = this.processVSHistory(event.body.data.finalPersistingState);
            }
        }
    }

    setWinDisplayConfigForSpinType(type: SpinType): WinDisplayConfig {

        this._activeWinDisplayConfig = this.winDisplayConfigs.get(type) as WinDisplayConfig;
        return this._activeWinDisplayConfig;
    }

    applyBonusOverlaySymbols(): void {
        this.reels.applyBonusOverlaySymbols();
    }

    override reset(): void {
        super.reset();
        reset(this.resetList);

        this._openGameEvent = undefined;

        this.state = GameState.INIT;
        this.spinSpeedType = SpinSpeedType.REGULAR;
        this.spinModeType = SpinModeType.MANUAL;
        this.bigWin = false;
        this.vsPickIndex = undefined;
        this.vsResultData = undefined;
        this.haveEnteredFreeSpins = false;
    }

    private setupWinDisplayConfigs(): void {

        const configs: Record<SpinType, WinDisplayConfig> = this.services.resources.getSetting(`winDisplayConfigs`);

        for (const [key, value] of Object.entries(configs)) {

            switch(key) {
                case SpinType.REGULAR:
                    this.winDisplayConfigs.set(key, value);
                    break;
                case SpinType.VS:
                case SpinType.FREE_SPIN:
                case SpinType.TURBO:
                case SpinType.TURBO_VS:
                case SpinType.TURBO_FREE_SPIN:
                    this.winDisplayConfigs.set(key, this.makeCustomWinDisplayConfig(configs[SpinType.REGULAR], value));
                    break;
                default:
                    throw new Error(`Unknown ReelSpinSettings ${key}`);
            }
        }
    }

    private makeCustomWinDisplayConfig(defaultValues: Record<string,any>, customValues: Record<string,any>): WinDisplayConfig {

        const customConfig = cloneDeep(defaultValues);

        for (const [key, value] of Object.entries(customValues)) {
            customConfig[key] = value;
        }

        return customConfig as WinDisplayConfig;
    }

    private processVSResults(dragonsWins: PayLineWin[], pandasWins: PayLineWin[], pick: VSOption, jackpotWin: number): VSResultData {

        let dragonsWinTotal = dragonsWins.reduce((a, item) => a + item.moneyWon, 0);
        let pandasWinTotal = pandasWins.reduce((a, item) => a + item.moneyWon, 0);

        if (pick === VSOption.DRAGON) {
            dragonsWinTotal += jackpotWin;

        } else {
            pandasWinTotal += jackpotWin;
        }

        let result;

        if (dragonsWinTotal > pandasWinTotal) {
            result = VSResult.DRAGON_WIN;

        } else if (pandasWinTotal > dragonsWinTotal) {
            result = VSResult.PANDA_WIN;

        } else {
            result = VSResult.DRAW;
        }

        return {
            result,
            wins: {
                dragon: dragonsWinTotal,
                panda: pandasWinTotal
            },
            pick
        };
    }

    private processVSHistory(persistingStateData: FinalPersistingState): VSHistoryData {

        const spec = this.services.resources.getSpec(`vsscreen`);

        const dragonResults: VSHistoryResult[] = [];
        const pandaResults: VSHistoryResult[] = [];

        let startingIndex = 0;
        if (persistingStateData.gameHistory.length > spec.vsHistoryTargetGames) {
            startingIndex = persistingStateData.gameHistory.length - spec.vsHistoryTargetGames;
        }

        for(let i=startingIndex; i < persistingStateData.gameHistory.length; i++) {
            const dragonScore: number = persistingStateData.gameHistory[i].dragonsSideMoneyWon;
            const pandaScore: number = persistingStateData.gameHistory[i].pandasSideMoneyWon;

            if (dragonScore > pandaScore) {
                dragonResults.push({
                    value: dragonScore,
                    type: `WIN`
                });
                pandaResults.push({
                    value: pandaScore,
                    type: `LOSS`
                });
            } else if (pandaScore > dragonScore) {
                dragonResults.push({
                    value: dragonScore,
                    type: `LOSS`
                });
                pandaResults.push({
                    value: pandaScore,
                    type: `WIN`
                });
            } else {
                dragonResults.push({
                    value: dragonScore,
                    type: `DRAW`
                });
                pandaResults.push({
                    value: pandaScore,
                    type: `DRAW`
                });
            }
        }

        return {
            vsResults: {
                dragons: dragonResults,
                pandas: pandaResults
            }
        };
    }

    //------Free Rounds------
    private _freeRoundData?: FREE_ROUND;

    get isFreeRound() {
        return this._freeRoundData !== undefined;
    }

    get activeFreeRoundPending() {
        return this._freeRoundData && this._freeRoundData.status === `PENDING`;
    }

    get activeFreeRoundsFinished() {
        return this._freeRoundData && this._freeRoundData.status === `FINISHED`;
    }

    get activeFreeRoundData() {
        if (!this._freeRoundData) {
            throw new Error(`Missing main model freeRoundData`);
        }
        return this._freeRoundData;
    }

    get freeRoundId(): string | undefined {
        return this.isFreeRound ? this.activeFreeRoundData.id : undefined;
    }

    get winLineCount(): number {

        return this.winlines.dragons.lineCount + this.winlines.pandas.lineCount;
    }

    get hasScatterWins(): boolean {
        return this.winlines.dragons.hasScatterWin || this.winlines.pandas.hasScatterWin ? true : false;
    }

    setFreeRoundData(data: FREE_ROUND) {
        this._freeRoundData = data;
    }

    setSpinCompleteWaiter(accumulatedMoneyWon: number): void {
        this.spinCompleteWaiter.waiting = true;
        this.spinCompleteWaiter.accumulatedMoneyWon = accumulatedMoneyWon;
    }

    clearSpinCompleteWaiter(): void {
        this.spinCompleteWaiter.waiting = false;
    }

    getSpinCompleteWaiter(): SpinCompleteWaiter {
        return this.spinCompleteWaiter;
    }

    freeRoundsComplete() {
        this._freeRoundData = undefined;
    }
}